import React, { memo, useState, useRef, useMemo } from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import type { NodeProps } from '@xyflow/react';
import { useTenantView } from '../TenantViewContext';
import { getNodePulseLevel, isNodeWiredToLunaCore } from '../pulseUtils';
import { Mail, Send, Eye, EyeOff, Server, Shield, Sparkles, Zap, AlertCircle, ArrowRight } from 'lucide-react';

export interface EmailSmtpNodeData {
  smtpHost?: string;
  smtpPort?: number | string;
  smtpUser?: string;
  smtpPassword?: string;
  senderName?: string;
  triggerEvent?: 'on_user_register' | 'on_password_reset' | 'on_form_submitted' | 'custom_ai_trigger';
  subjectTemplate?: string;
  emailTemplate?: string;
  isCustomizable?: boolean;
  [key: string]: unknown;
}

export const EmailSmtpNode = memo(({ id, data, selected }: NodeProps) => {
  const { setNodes, getEdges, getNodes } = useReactFlow();
  const nodeData = (data || {}) as EmailSmtpNodeData;
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [saveIndicator, setSaveIndicator] = useState<string | null>(null);
  const debounceTimerRef = useRef<any>(null);

  const { isTenantView, authToken, tenantApiKey, pulseState, openPortalData } = useTenantView();

  const persistNodeDataDirectly = (field: keyof EmailSmtpNodeData, value: any, allUpdates?: Partial<EmailSmtpNodeData>) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const payloadData = {
          ...nodeData,
          [field]: value,
          ...(allUpdates || {})
        };

        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };

        const token = authToken || (typeof localStorage !== 'undefined' ? localStorage.getItem('tenet_token') || localStorage.getItem('admin_token') : null);
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        if (tenantApiKey) {
          headers['x-tenant-api-key'] = tenantApiKey;
        }

        const endpoint = isTenantView ? '/api/tenet/widgets/save-node-data' : '/api/admin/luna-flows/save-node-data';
        const res = await fetch(endpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            nodeId: id,
            widgetType: 'EMAIL_SMTP',
            data: payloadData,
            widgetData: payloadData,
          }),
        });

        if (res.ok) {
          setSaveIndicator('✅ Saved to DB');
          setTimeout(() => setSaveIndicator(null), 3000);
        }
      } catch (err) {
        console.warn('[EmailSmtpNode] Direct persist warning:', err);
      }
    }, 600);
  };

  const handleChange = (field: keyof EmailSmtpNodeData, value: any) => {
    setNodes((nds) =>
      nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, [field]: value } } : n))
    );
    persistNodeDataDirectly(field, value);
  };

  const pulseLevel = getNodePulseLevel(id, pulseState);
  const isNodeActive = pulseLevel === 'active';
  const isNodeTrail = pulseLevel === 'trail';
  const isWired = useMemo(() => isNodeWiredToLunaCore(id, getEdges(), getNodes()), [id, getEdges, getNodes]);

  return (
    <div
      className={`group rounded-2xl border transition-all duration-300 w-[380px] select-none relative backdrop-blur-xl bg-gradient-to-b from-[#0F0E28]/98 via-[#09081B]/98 to-[#04040E]/98 shadow-[0_20px_50px_rgba(0,0,0,0.85),inset_0_1px_0_0_rgba(255,255,255,0.12)] ${
        isNodeActive
          ? 'border-cyan-300 ring-4 ring-cyan-400/60 shadow-[0_0_80px_rgba(34,211,238,0.8),inset_0_1px_0_0_rgba(255,255,255,0.2)] scale-[1.02] pulse-glow-active'
          : isNodeTrail
          ? 'border-emerald-400/70 ring-2 ring-emerald-400/30 shadow-[0_0_40px_rgba(52,211,153,0.4),inset_0_1px_0_0_rgba(255,255,255,0.15)]'
          : selected
          ? 'border-indigo-400 ring-2 ring-indigo-400/50 shadow-[0_0_35px_rgba(99,102,241,0.5),inset_0_1px_0_0_rgba(255,255,255,0.18)]'
          : isWired
          ? 'border-indigo-500/40 hover:border-indigo-400 shadow-[0_20px_50px_rgba(0,0,0,0.85),0_0_20px_rgba(99,102,241,0.15),inset_0_1px_0_0_rgba(255,255,255,0.12)]'
          : 'border-white/[0.08] hover:border-white/[0.18]'
      }`}
    >
      {/* ── Floating Cyber-HUD Action: View Live Data in Portal (Fades in on Hover) ── */}
      {openPortalData && (
        <div className="absolute -top-11 left-1/2 -translate-x-1/2 z-50 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all duration-300 transform translate-y-1 group-hover:translate-y-0">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openPortalData('actionNode', id);
            }}
            className="nodrag flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-indigo-950 via-slate-900 to-purple-950 border-2 border-indigo-400 text-indigo-200 font-extrabold text-[11px] uppercase tracking-wider shadow-[0_0_25px_rgba(99,102,241,0.8)] hover:border-indigo-300 hover:scale-105 hover:text-white transition-all cursor-pointer whitespace-nowrap backdrop-blur-md active:scale-95"
            title="Open Portal Data to configure actions and email dispatches"
          >
            <Mail size={13} className="text-indigo-300 animate-pulse" />
            <span>View Data in Portal</span>
            <ArrowRight size={13} className="text-indigo-400" />
          </button>
        </div>
      )}
      {/* ── Directional Connection Handles (Left in from Luna Core / Right out) ── */}
      <Handle
        type="target"
        position={Position.Left}
        id="in-email"
        className="!bg-purple-400 !w-4 !h-4 !border-2 !border-slate-950 !-left-2 transition-transform hover:scale-125 z-20 shadow-[0_0_12px_rgba(168,85,247,1)]"
        title="Input: Trigger from Luna Core Hub (out-actions)"
      />
      <Handle
        type="source"
        position={Position.Right}
        id="out-email"
        className="!bg-purple-400 !w-4 !h-4 !border-2 !border-slate-950 !-right-2 transition-transform hover:scale-125 z-20 shadow-[0_0_12px_rgba(168,85,247,1)]"
        title="Output: Chained Subsequent Actions"
      />
      <Handle
        type="target"
        position={Position.Top}
        id="in-top"
        className="!bg-purple-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-top-2 transition-transform hover:scale-125 shadow-[0_0_8px_rgba(168,85,247,0.8)]"
      />

      {/* ── Node Header ──────────────────────────────────────────────── */}
      <div className="bg-gradient-to-r from-indigo-950/80 via-purple-950/60 to-slate-950/80 px-4 py-3 border-b border-indigo-500/30 flex items-center justify-between rounded-t-2xl">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-lg bg-indigo-500/20 border border-indigo-400/40 text-indigo-300 shadow-[0_0_12px_rgba(99,102,241,0.3)]">
            <Mail size={16} />
          </div>
          <div>
            <h4 className="text-xs font-black text-slate-100 tracking-wide">Email & SMTP Server</h4>
            <p className="text-[10px] text-indigo-300/70 font-mono">Autonomous Mail Dispatch</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {saveIndicator && (
            <span className="text-[9px] font-mono font-bold text-emerald-400 animate-pulse bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/30">
              {saveIndicator}
            </span>
          )}
          <span className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
            SMTP ACTION
          </span>
        </div>
      </div>

      {/* Wiring Live Status Bar */}
      <div className="mx-4 mt-3">
        {isWired ? (
          <div className="px-2.5 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] flex items-center justify-between font-mono">
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <Zap size={11} className="text-emerald-400" /> Wired to Luna Core Hub
            </span>
            <span className="text-[9px] font-bold text-emerald-400 uppercase tracking-wide">ACTIVE</span>
          </div>
        ) : (
          <div className="px-2.5 py-1 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-[10px] flex items-center justify-between font-mono">
            <span className="flex items-center gap-1.5">
              <AlertCircle size={11} className="text-amber-400" /> Disconnected
            </span>
            <span className="text-[9px] text-amber-400/80">Connect to Luna Core Hub</span>
          </div>
        )}
      </div>

      {/* ── Node Body & Parameter Inputs ──────────────────────────────── */}
      <div className="p-4 space-y-3.5 text-xs">
        
        {/* Trigger Event Selector */}
        <div className="space-y-1">
          <label className="text-[10px] font-mono uppercase tracking-wider text-slate-400 font-semibold flex items-center justify-between">
            <span>Trigger Event</span>
            <span className="text-indigo-400 text-[9px]">LUNA HOOK</span>
          </label>
          <select
            value={nodeData.triggerEvent || 'on_user_register'}
            onChange={(e) => handleChange('triggerEvent', e.target.value)}
            className="w-full bg-slate-900/90 border border-slate-700/80 rounded-xl px-3 py-2 text-slate-100 text-xs font-medium focus:outline-none focus:border-indigo-400 transition"
          >
            <option value="on_user_register">On New User Registration</option>
            <option value="on_password_reset">On Password Reset Request</option>
            <option value="on_form_submitted">On Form / Lead Submitted</option>
            <option value="custom_ai_trigger">Custom Autonomous AI Trigger</option>
          </select>
        </div>

        {/* SMTP Server Host & Port */}
        <div className="grid grid-cols-3 gap-2">
          <div className="col-span-2 space-y-1">
            <label className="text-[10px] font-mono uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1">
              <Server size={10} className="text-indigo-400" /> SMTP Host
            </label>
            <input
              type="text"
              value={nodeData.smtpHost || ''}
              onChange={(e) => handleChange('smtpHost', e.target.value)}
              placeholder="smtp.gmail.com"
              className="w-full bg-slate-900/90 border border-slate-700/80 rounded-xl px-3 py-1.5 text-slate-100 text-xs font-mono focus:outline-none focus:border-indigo-400 transition"
            />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-mono uppercase tracking-wider text-slate-400 font-semibold">
              Port
            </label>
            <input
              type="text"
              value={nodeData.smtpPort || '587'}
              onChange={(e) => handleChange('smtpPort', e.target.value)}
              placeholder="587"
              className="w-full bg-slate-900/90 border border-slate-700/80 rounded-xl px-3 py-1.5 text-slate-100 text-xs font-mono text-center focus:outline-none focus:border-indigo-400 transition"
            />
          </div>
        </div>

        {/* SMTP Username / Email */}
        <div className="space-y-1">
          <label className="text-[10px] font-mono uppercase tracking-wider text-slate-400 font-semibold">
            SMTP Username / Email
          </label>
          <input
            type="email"
            value={nodeData.smtpUser || ''}
            onChange={(e) => handleChange('smtpUser', e.target.value)}
            placeholder="notifications@company.com"
            className="w-full bg-slate-900/90 border border-slate-700/80 rounded-xl px-3 py-1.5 text-slate-100 text-xs focus:outline-none focus:border-indigo-400 transition"
          />
        </div>

        {/* Obscured SMTP Password */}
        <div className="space-y-1">
          <label className="text-[10px] font-mono uppercase tracking-wider text-slate-400 font-semibold flex items-center justify-between">
            <span className="flex items-center gap-1">
              <Shield size={10} className="text-indigo-400" /> SMTP Password
            </span>
            <span className="text-[9px] text-emerald-400 font-mono">ENCRYPTED VAULT</span>
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={nodeData.smtpPassword || ''}
              onChange={(e) => handleChange('smtpPassword', e.target.value)}
              placeholder="••••••••••••••••"
              className="w-full bg-slate-900/90 border border-slate-700/80 rounded-xl pl-3 pr-9 py-1.5 text-slate-100 text-xs font-mono focus:outline-none focus:border-indigo-400 transition"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-indigo-300 transition cursor-pointer"
            >
              {showPassword ? <EyeOff size={13} /> : <Eye size={13} />}
            </button>
          </div>
        </div>

        {/* Sender Name & Subject Template */}
        <div className="space-y-1">
          <label className="text-[10px] font-mono uppercase tracking-wider text-slate-400 font-semibold">
            Sender Display Name
          </label>
          <input
            type="text"
            value={nodeData.senderName || ''}
            onChange={(e) => handleChange('senderName', e.target.value)}
            placeholder='e.g. "Success Mind Support"'
            className="w-full bg-slate-900/90 border border-slate-700/80 rounded-xl px-3 py-1.5 text-slate-100 text-xs focus:outline-none focus:border-indigo-400 transition"
          />
        </div>

        {/* Subject Template */}
        <div className="space-y-1">
          <label className="text-[10px] font-mono uppercase tracking-wider text-slate-400 font-semibold">
            Subject Template
          </label>
          <input
            type="text"
            value={nodeData.subjectTemplate || ''}
            onChange={(e) => handleChange('subjectTemplate', e.target.value)}
            placeholder="Welcome to {{tenantName}} Client Portal"
            className="w-full bg-slate-900/90 border border-slate-700/80 rounded-xl px-3 py-1.5 text-slate-100 text-xs focus:outline-none focus:border-indigo-400 transition font-mono"
          />
        </div>

        {/* Email Body Template */}
        <div className="space-y-1">
          <label className="text-[10px] font-mono uppercase tracking-wider text-slate-400 font-semibold flex items-center justify-between">
            <span>Email Body Template (HTML / Markdown)</span>
            <span className="text-[9px] text-indigo-400 font-mono">TEMPLATE</span>
          </label>
          <textarea
            rows={3}
            value={nodeData.emailTemplate || ''}
            onChange={(e) => handleChange('emailTemplate', e.target.value)}
            placeholder="Hello {{clientName}},\n\nYour secure client portal is ready: {{portalUrl}}\n\nBest regards,\n{{tenantName}} Support Team"
            className="w-full bg-slate-900/90 border border-slate-700/80 rounded-xl px-3 py-2 text-slate-100 text-xs focus:outline-none focus:border-indigo-400 transition resize-none leading-relaxed font-mono"
          />
        </div>
      </div>
    </div>
  );
});

EmailSmtpNode.displayName = 'EmailSmtpNode';
export default EmailSmtpNode;
